import { useEffect, useState } from "react";

import purify from "dompurify";
import { useTranslation } from "pro-features/components/WithTranslation";

import { Button, Modal } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { DunningsBulletPoint } from "app/components/Dunnings/DunningsBulletPoint/DunningsBulletPoint";
import {
    DUNNINGS_INLINE_BANNER_CTA_LOCATION,
    DUNNINGS_RESTART_TRIAL_EVENT_SUB_SUB_NAME,
    RESTART_TRIAL_DUNNINGS_EVENT_NAME,
} from "app/components/Dunnings/dunningsConstants";
import { DunningsSectionWrapper } from "app/components/Dunnings/DunningsStyled";
import { useDunnings } from "app/components/hooks/useDunnings";
import { afterTrialBannerModalStyles } from "app/common/after-trial/afterTrialBannerModalStyles";
import { AfterTrialPopupContainer } from "app/components/React/AfterTrialPopup/AfterTrialPopupContainer";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { isSI } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

export const RestartTrialDunningsSection = () => {
    const translate = useTranslation();
    const { onDunningsButtonClicked } = useDunnings();
    const [isStartTrialModalOpen, setIsStartTrialModalOpen] = useState(false);

    useEffect(() => {
        allTrackers.dispatchTrackEvent(
            "banner",
            "show",
            `${RESTART_TRIAL_DUNNINGS_EVENT_NAME}/${DUNNINGS_INLINE_BANNER_CTA_LOCATION}`,
        );
    }, []);

    const onClick = () => {
        onDunningsButtonClicked(
            `${RESTART_TRIAL_DUNNINGS_EVENT_NAME}/${DUNNINGS_INLINE_BANNER_CTA_LOCATION}/${DUNNINGS_RESTART_TRIAL_EVENT_SUB_SUB_NAME}`,
        );
        setIsStartTrialModalOpen(true);
    };

    return (
        <>
            <DunningsSectionWrapper data-automation="restart-trial-dunnings-section">
                <Stack gap="20px">
                    <Stack gap="12px">
                        <Text
                            fontVariant="h4"
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: purify.sanitize(translate("dunnings.restartTrial.modal.title")),
                            }}
                        />

                        <Text
                            fontVariant="body2"
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: purify.sanitize(translate("dunnings.restartTrial.section.text")),
                            }}
                        />
                    </Stack>

                    <Stack gap="12px">
                        <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_1")} />

                        <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_2")} />

                        <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_3")} />
                    </Stack>

                    <Button
                        width="fit-content"
                        type="primary"
                        onClick={onClick}
                    >
                        {translate("dunnings.restartTrial.modal.restart_trial")}
                    </Button>
                </Stack>

                <img
                    alt={translate("dunnings.restartTrial.modal.image_alt")}
                    src={AssetsService.assetUrl("/images/dunnings/reactivate_modal_image.svg")}
                />
            </DunningsSectionWrapper>

            <Modal
                isOpen={isStartTrialModalOpen}
                onRequestClose={() => setIsStartTrialModalOpen(false)}
                customStyles={afterTrialBannerModalStyles}
                shouldCloseOnOverlayClick={true}
            >
                <AfterTrialPopupContainer
                    setIsOpen={setIsStartTrialModalOpen}
                    isSI={isSI()}
                />
            </Modal>
        </>
    );
};
